import { classNames } from '@fullstory/util/fmt';
import React, { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { identifyComponent } from '../../util';
import cssAlign from './align.module.scss';
import cssAppearance from './appearance.module.scss';
import cssColor from './color.module.scss';
import css from './index.module.scss';

const appearanceTagMap = {
  title1: 'h1',
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  bodyLarge: 'p',
  bodyLargeBold: 'p',
  bodyMedium: 'p',
  bodyMediumBold: 'p',
  bodySmall: 'p',
  bodySmallBold: 'p',
  bodyXSmall: 'p',
  bodyXSmallBold: 'p',
  mono1: 'pre',
  mono2: 'pre',
  mono3: 'pre',
} as const;

export type TextAppearance = keyof typeof appearanceTagMap;

export type Color = keyof typeof cssColor;
type Align = keyof typeof cssAlign;

export interface TextProps<A extends TextAppearance, T extends ElementType> {
  /**
   * The size, weight, and line height of the rendered text. Matches 1 to 1 with
   * the Figma UI Kit Text Styles
   */
  appearance: A;
  /**
   * The alignment of the rendered text. Defaults to `inherit` to inherit the
   * alignment set by an element higher up in the DOM tree.
   */
  align?: Align;
  /**
   * The semantic color of the text. Can be set to `inherit` to
   * inherit the color set by an element higher up in the DOM tree.
   */
  color?: Color;
  /**
   * Element used for rendering the text. (eg. `p` or `span`). The default if
   * not provided is dependent on the `appearance` prop.
   *
   * titles and headings will render `h1`, `h2`, or `h3`,
   * bodys will render `p`, and
   * monos will render `pre`
   */
  tag?: T;
  children?: ReactNode;
  /**
   * Determines if the text should ellipsize when constrained instead of wrapping.
   */
  noWrap?: boolean;
  testId?: string;
}

export function Text<
  A extends TextAppearance,
  T extends ElementType = (typeof appearanceTagMap)[A],
>({
  appearance,
  align = 'inherit',
  color = 'default',
  tag,
  className,
  noWrap = false,
  testId = 'text',
  ...props
}: TextProps<A, T> & Omit<ComponentPropsWithoutRef<T>, keyof TextProps<A, T>>) {
  const Component = (tag || appearanceTagMap[appearance]) as ElementType;
  return (
    <Component
      data-component="Text"
      className={classNames(
        className,
        css.text,
        cssAppearance[appearance],
        color ? cssColor[color] : null,
        align ? cssAlign[align] : null,
        {
          [css.noWrap]: noWrap,
        },
      )}
      data-testid={testId}
      {...props}
      {...identifyComponent('text', props)}
    />
  );
}
